/**
 * Shared rocket-nozzle display helpers.
 *
 * Behaviour-preserving extraction (issue #128): the input-parsing + unit
 * conversion wrapper around `calculateRocketNozzle`, plus the `parseNum` /
 * `fmt` formatting helpers, were byte-identical across
 * `src/components/ParameterArea.tsx` and
 * `src/components/featureTreeSidebar/SpaceContent.tsx`. They live here so both
 * call sites share one copy. The underlying physics already lives in
 * `src/lib/rocketNozzle.ts`; this module only owns the React-side glue.
 *
 * NOTE: callers keep their own `useMemo(() => computeRocketNozzleOutputs(...),
 * [inputs])` so React hook ordering is unchanged.
 */

import { calculateRocketNozzle } from '@/lib/rocketNozzle';
import {
  ft2ToM2,
  m2ToFt2,
  psiToPa,
  paToPsi,
  msToFps,
  kgToLb,
  nToLbf,
  fahrenheitToKelvin,
} from '@/lib/units';

/** The eight user-editable string fields the calculation reads. */
export interface RocketNozzleInputValues {
  At_ft2: string;
  AeAt: string;
  AiAt: string;
  Pt_psi: string;
  P0_psi: string;
  Tt_F: string;
  gamma: string;
  MW: string;
}

/** Derived performance values in display units (or `null` when inputs invalid). */
export interface RocketNozzleDisplayOutputs {
  Mt: number;
  Me: number;
  Pe_psi: number;
  Ue_fps: number;
  mdot_lbps: number;
  F_lbs: number;
  Isp_s: number;
  NPR: number;
  cstar_fps: number;
  CF: number;
  Ae_ft2: number;
}

export function parseNum(v: string): number {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : NaN;
}

export function fmt(v: number, sig = 4): string {
  if (!Number.isFinite(v)) return '—';
  if (v === 0) return '0';
  const abs = Math.abs(v);
  if (abs < 1e-3 || abs >= 1e7) return v.toExponential(sig - 1);
  return Number(v.toPrecision(sig)).toString();
}

/**
 * Parse the input strings, run the quasi-1D nozzle calculation, and map the SI
 * result back into display units. Returns `null` when any input is invalid
 * (non-finite, non-positive, ε ≤ 1, or γ ≤ 1) or the calculation throws.
 *
 * Body is byte-equivalent to the former inlined `useMemo` callbacks in
 * ParameterArea and SpaceContent.
 */
export function computeRocketNozzleOutputs(
  inputs: RocketNozzleInputValues,
): RocketNozzleDisplayOutputs | null {
  const At_ft2 = parseNum(inputs.At_ft2);
  const AeAt = parseNum(inputs.AeAt);
  const Pt_psi = parseNum(inputs.Pt_psi);
  const P0_psi = parseNum(inputs.P0_psi);
  const Tt_F = parseNum(inputs.Tt_F);
  const gamma = parseNum(inputs.gamma);
  const MW = parseNum(inputs.MW);

  if (
    ![At_ft2, AeAt, Pt_psi, P0_psi, Tt_F, gamma, MW].every(
      (v) => Number.isFinite(v) && v > 0,
    ) ||
    AeAt <= 1 ||
    gamma <= 1
  ) {
    return null;
  }

  try {
    const r = calculateRocketNozzle({
      At: ft2ToM2(At_ft2),
      AeAt,
      Pt: psiToPa(Pt_psi),
      Pamb: psiToPa(P0_psi),
      Tt: fahrenheitToKelvin(Tt_F),
      gamma,
      MW,
    });
    return {
      Mt: r.Mt,
      Me: r.Me,
      Pe_psi: paToPsi(r.Pe),
      Ue_fps: msToFps(r.Ve),
      mdot_lbps: kgToLb(r.mdot),
      F_lbs: nToLbf(r.F),
      Isp_s: r.Isp,
      NPR: r.NPR,
      cstar_fps: msToFps(r.cstar),
      CF: r.CF,
      Ae_ft2: m2ToFt2(r.Ae),
    };
  } catch {
    return null;
  }
}
